import * as THREE from 'three';

/**
 * RecordingManager - Gestionnaire d'enregistrement vidéo SIMPLE
 * Enregistre seulement le canvas Three.js directement
 * Crée temporairement un background vidéo si postprocess n'est pas actif
 */
export class RecordingManager {
    constructor(threejsCanvas, videoCanvas = null, qualityPreset = 'archive') {
        this.threejsCanvas = threejsCanvas;
        this.videoCanvas = videoCanvas;
        this.mediaRecorder = null;
        this.recordedChunks = [];
        this.isRecording = false;
        this.recordButton = null;
        this.pictureButton = null; // Nouveau bouton pour les photos
        this.startTime = null;
        
        // Pour le background vidéo temporaire
        this.temporaryVideoBackground = null;
        this.threeScene = null; // Référence à la scène Three.js
        this.currentStream = null; // Référence au stream de capture
        
        // Configuration de qualité avec détection automatique du format optimal
        this.qualityPresets = {
            'low': {
                fps: 24,
                bitrate: 1000000,  // 1 Mbps
                mimeType: this.getBestMimeType(['video/mp4;codecs=avc1.42E01E', 'video/webm;codecs=vp8']),
                chunkInterval: 200,
                description: 'Basse qualité (1 Mbps, 24 FPS)'
            },
            'medium': {
                fps: 30,
                bitrate: 2000000,  // 2 Mbps
                mimeType: this.getBestMimeType(['video/mp4;codecs=avc1.640028', 'video/webm;codecs=vp9']),
                chunkInterval: 100,
                description: 'Qualité moyenne (2 Mbps, 30 FPS)'
            },
            'high': {
                fps: 30,
                bitrate: 2500000,  // 2.5 Mbps
                mimeType: this.getBestMimeType(['video/mp4;codecs=avc1.64002A', 'video/webm;codecs=vp9']),
                chunkInterval: 100,
                description: 'Haute qualité (2.5 Mbps, 30 FPS)'
            },
            'ultra': {
                fps: 60,
                bitrate: 5000000,  // 5 Mbps
                mimeType: this.getBestMimeType(['video/mp4;codecs=avc1.64002A', 'video/webm;codecs=vp9']),
                chunkInterval: 50,
                description: 'Ultra qualité (5 Mbps, 60 FPS)'
            },
            'lossless': {
                fps: 30,
                bitrate: 50000000,  // 50 Mbps - Quasi sans perte
                mimeType: this.getBestMimeType(['video/mp4;codecs=avc1.64002A', 'video/webm;codecs=vp9']),
                chunkInterval: 100,
                description: 'Quasi sans perte (50 Mbps, 30 FPS)'
            },
            'archive': {
                fps: 30,
                bitrate: 100000000,  // 100 Mbps - Qualité archivage
                mimeType: this.getBestMimeType(['video/mp4;codecs=avc1.64002A', 'video/webm;codecs=vp9']),
                chunkInterval: 50,
                description: 'Qualité archivage (100 Mbps, 30 FPS)'
            }
        };
        
        this.currentQuality = this.qualityPresets[qualityPreset] || this.qualityPresets['archive'];
        
        // Détection du navigateur et affichage des infos de compatibilité
        this.logBrowserCompatibility();
        
        this.initializeUI();
    }

    /**
     * Détecte le meilleur MIME type supporté par le navigateur
     * Priorité : MP4/H.264 (pour iOS) > WebM/VP9 > WebM/VP8
     */
    getBestMimeType(candidates) {
        for (const mimeType of candidates) {
            if (MediaRecorder.isTypeSupported(mimeType)) {
                return mimeType;
            }
        }
        
        // Fallback si aucun candidat n'est supporté
        const fallbacks = [
            'video/mp4',
            'video/webm',
            'video/mp4;codecs=avc1.42E01E',  // H.264 Baseline
            'video/webm;codecs=vp8'
        ];
        
        for (const fallback of fallbacks) {
            if (MediaRecorder.isTypeSupported(fallback)) {
                return fallback;
            }
        }
        
        // Dernier recours
        return 'video/webm';
    }

    /**
     * Affiche les informations de compatibilité du navigateur
     */
    logBrowserCompatibility() {
        // Method kept for backwards compatibility but console outputs removed
    }

    /**
     * Définit la référence à la scène Three.js (appelé depuis main.js)
     */
    setThreeScene(threeScene) {
        this.threeScene = threeScene;
        threeScene.onSceneChangedCallback = async () => {
            if (this.isRecording) {
                // The old temporary background was removed when the scene cleared its children.
                // Null out the stale reference so createTemporaryVideoBackground() can run again.
                this.temporaryVideoBackground = null;
                await this.createTemporaryVideoBackground();
            }
        };
    }

    /**
     * Change la qualité d'enregistrement
     */
    setQuality(qualityPreset) {
        if (this.isRecording) {
            return false;
        }
        
        const quality = this.qualityPresets[qualityPreset];
        if (!quality) {
            return false;
        }
        
        this.currentQuality = quality;
        
        // Mettre à jour le titre du bouton
        this.recordButton.title = `Record at ${this.currentQuality.description}`;
        
        return true;
    }

    /**
     * Force l'utilisation d'un format spécifique (utile pour les tests ou besoins spéciaux)
     */
    setVideoFormat(format) {
        if (this.isRecording) {
            return false;
        }

        const formats = {
            'mp4': 'video/mp4;codecs=avc1.64002A',
            'mp4-baseline': 'video/mp4;codecs=avc1.42E01E', 
            'mp4-main': 'video/mp4;codecs=avc1.640028',
            'webm-vp8': 'video/webm;codecs=vp8',
            'webm-vp9': 'video/webm;codecs=vp9',
            'webm': 'video/webm'
        };

        const mimeType = formats[format.toLowerCase()];
        if (!mimeType) {
            return false;
        }

        if (!MediaRecorder.isTypeSupported(mimeType)) {
            return false;
        }

        // Mettre à jour tous les presets
        for (const preset of Object.values(this.qualityPresets)) {
            preset.mimeType = mimeType;
        }

        this.logBrowserCompatibility(); // Re-log les infos
        return true;
    }

    /**
     * Obtient les presets de qualité disponibles
     */
    getQualityPresets() {
        return Object.keys(this.qualityPresets).map(key => ({
            key,
            ...this.qualityPresets[key]
        }));
    }

    /**
     * Crée un background vidéo temporaire si le postprocess n'est pas actif
     */
    async createTemporaryVideoBackground() {
        if (!this.threeScene || !this.videoCanvas) {
            return false;
        }

        // Vérifier si le postprocess est actif (donc si un videoBG existe déjà)
        const hasExistingVideoBackground = this.threeScene.videoBackground !== null;
        
        if (hasExistingVideoBackground) {
            return false; // Pas besoin de créer un background temporaire
        }

        try {
            // Import dynamique de VideoPlane
            const { VideoPlane } = await import('../scene/objects/video_bg.js');
            
            // Créer un background similaire à celui du postprocess mais temporaire
            this.temporaryVideoBackground = new VideoPlane(
                this.threeScene.scene,
                this.threeScene.width,
                this.threeScene.height,
                this.threeScene.project_settings,
                "temporaryVideoBackground", // Nom différent pour éviter les conflits
                new THREE.Vector3(0, 0, -1500), // Même position que le videoBG standard
                "screenSpaceMask", // Utiliser le matériau screenSpaceMask qui affiche la vidéo
                this.threeScene.width,
                this.threeScene.height,
                -1 // Render order en arrière-plan
            );

            // IMPORTANT: Remplacer le matériau par celui qui affiche la texture vidéo
            if (this.threeScene.materialManager) {
                
                // Obtenir le matériau screenSpaceMask qui contient la texture vidéo
                const videoMaterial = this.threeScene.materialManager.getMaterialByName('screenSpaceMask');
                
                if (videoMaterial) {
                    // Remplacer le matériau du plane
                    this.temporaryVideoBackground.plane.material = videoMaterial;
                    this.temporaryVideoBackground.material = videoMaterial;
                }
            }

            return true;
        } catch (error) {
            return false;
        }
    }

    /**
     * Supprime le background vidéo temporaire
     */
    removeTemporaryVideoBackground() {
        if (this.temporaryVideoBackground && this.threeScene) {
            
            // Retirer le plane de la scène
            this.threeScene.scene.remove(this.temporaryVideoBackground.plane);
            
            // Nettoyer les ressources
            if (this.temporaryVideoBackground.material) {
                this.temporaryVideoBackground.material.dispose();
            }
            if (this.temporaryVideoBackground.plane.geometry) {
                this.temporaryVideoBackground.plane.geometry.dispose();
            }
            
            this.temporaryVideoBackground = null;
            
            // Force un render pour s'assurer que la scène est mise à jour
            if (this.threeScene.renderer) {
                this.threeScene.renderer.render(this.threeScene.scene, this.threeScene.camera);
            }
        }
    }

    /**
     * Arrête proprement le stream de capture
     */
    stopCaptureStream() {
        if (this.currentStream) {
            
            // Arrêter tous les tracks
            this.currentStream.getTracks().forEach(track => {
                track.stop();
            });
            
            this.currentStream = null;
        }
    }

    /**
     * Initialise l'interface utilisateur (boutons d'enregistrement et de photo)
     */
    initializeUI() {
        // Conteneur horizontal pour les deux boutons
        this.buttonsContainer = document.createElement('div');
        this.buttonsContainer.id = 'recordingButtonsContainer';
        Object.assign(this.buttonsContainer.style, {
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            flexDirection: 'row',
            gap: '8px',
            zIndex: '9999',
        });
        document.body.appendChild(this.buttonsContainer);

        this.createRecordButton();
        this.createPictureButton();
    }

    /**
     * Crée le bouton d'enregistrement vidéo
     */
    createRecordButton() {
        // Créer le bouton d'enregistrement
        this.recordButton = document.createElement('button');
        this.recordButton.id = 'recordButton';
        this.recordButton.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style="vertical-align: middle; margin-right: 4px;"><circle cx="12" cy="12" r="8"/></svg>Record';
        this.recordButton.title = 'Record';
        
        // Styles du bouton
        Object.assign(this.recordButton.style, {
            backgroundColor: '#000000',
            color: '#ffffff',
            border: 'none',
            borderRadius: '20px',
            padding: '6px 12px',
            fontSize: '12px',
            fontWeight: 'bold',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            transition: 'all 0.3s ease',
            fontFamily: 'Arial, sans-serif',
            whiteSpace: 'nowrap'
        });

        // Effets hover
        this.recordButton.addEventListener('mouseenter', () => {
            if (!this.isRecording) {
                this.recordButton.style.backgroundColor = '#333333';
                this.recordButton.style.transform = 'scale(1.05)';
            }
        });

        this.recordButton.addEventListener('mouseleave', () => {
            if (!this.isRecording) {
                this.recordButton.style.backgroundColor = '#000000';
                this.recordButton.style.transform = 'scale(1)';
            } else {
                this.recordButton.style.backgroundColor = '#444444';
                this.recordButton.style.transform = 'scale(1)';
            }
        });

        // Événement de clic
        this.recordButton.addEventListener('click', () => {
            if (this.isRecording) {
                this.stopRecording();
            } else {
                this.startRecording();
            }
        });

        // Ajouter le bouton au conteneur
        this.buttonsContainer.appendChild(this.recordButton);
    }

    /**
     * Crée le bouton de capture d'image
     */
    createPictureButton() {
        // Créer le bouton de photo
        this.pictureButton = document.createElement('button');
        this.pictureButton.id = 'pictureButton';
        this.pictureButton.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: middle; margin-right: 4px;"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path><circle cx="12" cy="13" r="4"></circle></svg>Picture';
        this.pictureButton.title = 'Capture image';
        
        // Styles du bouton photo
        Object.assign(this.pictureButton.style, {
            backgroundColor: '#000000',
            color: '#ffffff',
            border: 'none',
            borderRadius: '20px',
            padding: '6px 12px',
            fontSize: '12px',
            fontWeight: 'bold',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            transition: 'all 0.3s ease',
            fontFamily: 'Arial, sans-serif',
            whiteSpace: 'nowrap'
        });

        // Effets hover pour le bouton photo
        this.pictureButton.addEventListener('mouseenter', () => {
            this.pictureButton.style.backgroundColor = '#333333';
            this.pictureButton.style.transform = 'scale(1.05)';
        });

        this.pictureButton.addEventListener('mouseleave', () => {
            this.pictureButton.style.backgroundColor = '#000000';
            this.pictureButton.style.transform = 'scale(1)';
        });

        // Événement de clic pour la photo
        this.pictureButton.addEventListener('click', () => {
            this.takePictureWithOptions('jpg', 0.8);
        });

        // Événement de clic droit pour capture alternative
        this.pictureButton.addEventListener('contextmenu', (e) => {
            e.preventDefault();
            this.takePictureAlternative('png', 1.0);
        });

        // Ajouter le bouton au conteneur
        this.buttonsContainer.appendChild(this.pictureButton);
    }

    /**
     * Démarre l'enregistrement SIMPLE - capture directe du threejsCanvas
     * Avec background vidéo temporaire si nécessaire
     */
    async startRecording() {
        try {
            if (!this.threejsCanvas) {
                return;
            }

            // ÉTAPE 1: Créer le background vidéo temporaire si nécessaire
            const temporaryBackgroundCreated = await this.createTemporaryVideoBackground();
            
            if (temporaryBackgroundCreated) {
                // Attendre un peu pour que le background soit visible
                await new Promise(resolve => setTimeout(resolve, 200));
            }

            this.isRecording = true;

            // ÉTAPE 2: CAPTURE DIRECTE du threejsCanvas avec FPS configuré
            const stream = this.threejsCanvas.captureStream(this.currentQuality.fps);
            this.currentStream = stream; // Garder la référence
            
            if (stream.getVideoTracks().length === 0) {
                this.removeTemporaryVideoBackground(); // Nettoyer en cas d'erreur
                this.resetButton();
                return;
            }
            
            // MediaRecorder avec qualité configurée
            const options = {
                mimeType: this.currentQuality.mimeType,
                videoBitsPerSecond: this.currentQuality.bitrate
            };

            // Fallback si le codec n'est pas supporté
            if (!MediaRecorder.isTypeSupported(options.mimeType)) {
                if (options.mimeType.includes('vp9')) {
                    options.mimeType = 'video/webm;codecs=vp8';
                } else {
                    options.mimeType = 'video/webm';
                }
            }

            this.mediaRecorder = new MediaRecorder(stream, options);
            this.recordedChunks = [];

            this.mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    this.recordedChunks.push(event.data);
                }
            };

            this.mediaRecorder.onstop = () => {
                // IMPORTANT: Arrêter le stream et supprimer le background temporaire avant de sauvegarder
                this.stopCaptureStream();
                this.removeTemporaryVideoBackground();
                this.saveRecording();
            };

            this.mediaRecorder.onerror = (event) => {
                this.stopCaptureStream();
                this.removeTemporaryVideoBackground(); // Nettoyer en cas d'erreur
                this.resetButton();
            };

            this.mediaRecorder.onstart = () => {
                this.startTime = Date.now();
            };

            this.mediaRecorder.start(this.currentQuality.timeslice);
            this.updateButtonToStop();

        } catch (error) {
            this.removeTemporaryVideoBackground(); // Nettoyer en cas d'erreur
            this.resetButton();
        }
    }

    /**
     * Arrête l'enregistrement
     */
    stopRecording() {
        if (this.mediaRecorder && this.isRecording) {
            const elapsed = Date.now() - this.startTime;
            if (elapsed < 1000) {
                return;
            }
            
            this.mediaRecorder.stop(); // Cela déclenchera onstop qui supprimera le background
            this.isRecording = false;
            
            // Nettoyage explicite immédiat
            this.stopCaptureStream();
            this.removeTemporaryVideoBackground();
        }
    }

    /**
     * Capture une image du canvas Three.js
     * Utilise la même logique que l'enregistrement vidéo pour le background
     */
    async takePicture() {
        try {
            if (!this.threejsCanvas) {
                return;
            }

            // Animation du bouton pour feedback visuel
            this.animatePictureButton();

            // ÉTAPE 1: Créer le background vidéo temporaire si nécessaire
            const temporaryBackgroundCreated = await this.createTemporaryVideoBackground();
            
            if (temporaryBackgroundCreated) {
                // Attendre un peu pour que le background soit visible
                await new Promise(resolve => setTimeout(resolve, 200));
            }

            // ÉTAPE 2: Capturer l'image depuis le canvas
            const imageDataUrl = this.threejsCanvas.toDataURL('image/png', 1.0);
            
            // ÉTAPE 3: Nettoyer le background temporaire immédiatement
            if (temporaryBackgroundCreated) {
                this.removeTemporaryVideoBackground();
            }

            // ÉTAPE 4: Sauvegarder l'image
            this.savePicture(imageDataUrl);

        } catch (error) {
            this.removeTemporaryVideoBackground(); // Nettoyer en cas d'erreur
        }
    }

    /**
     * Sauvegarde l'image capturée
     */
    savePicture(dataUrl) {
        const link = document.createElement('a');
        link.style.display = 'none';
        
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        link.download = `threejs_screenshot_${timestamp}.png`;
        link.href = dataUrl;
        
        document.body.appendChild(link);
        link.click();
        
        setTimeout(() => {
            document.body.removeChild(link);
        }, 100);
    }

    /**
     * Animation du bouton photo pour feedback visuel
     */
    animatePictureButton() {
        // Flash effect
        const originalBg = this.pictureButton.style.backgroundColor;
        this.pictureButton.style.backgroundColor = '#ffffff';
        this.pictureButton.style.color = '#4444ff';
        
        setTimeout(() => {
            this.pictureButton.style.backgroundColor = originalBg;
            this.pictureButton.style.color = 'white';
        }, 150);
    }

    /**
     * Capture une image avec des options personnalisées
     * @param {string} format - Format d'image ('png', 'jpeg', 'webp')
     * @param {number} quality - Qualité de l'image (0-1, pour jpeg et webp)
     */
    async takePictureWithOptions(format = 'jpg', quality = 1.0) {
        try {
            if (!this.threejsCanvas) {
                return;
            }

            // Animation du bouton pour feedback visuel
            this.animatePictureButton();

            // ÉTAPE 1: Créer le background vidéo temporaire si nécessaire (même pour les photos)
            const temporaryBackgroundCreated = await this.createTemporaryVideoBackground();
            
            if (temporaryBackgroundCreated) {
                // Attendre que le background soit bien rendu
                await new Promise(resolve => setTimeout(resolve, 300));
                
                // Force un rendu pour s'assurer que tout est à jour
                if (this.threeScene && this.threeScene.renderer && this.threeScene.camera) {
                    this.threeScene.renderer.render(this.threeScene.scene, this.threeScene.camera);
                }
            }

            // ÉTAPE 2: Attendre un frame supplémentaire pour s'assurer que le rendu est à jour
            await new Promise(resolve => requestAnimationFrame(resolve));

            // ÉTAPE 3: Capturer l'image selon le format
            let mimeType, extension;
            switch (format.toLowerCase()) {
                case 'jpeg':
                case 'jpg':
                    mimeType = 'image/jpeg';
                    extension = 'jpg';
                    break;
                case 'webp':
                    mimeType = 'image/webp';
                    extension = 'webp';
                    break;
                default:
                    mimeType = 'image/png';
                    extension = 'png';
                    quality = 1.0; // PNG ignore la qualité
            }

            // Capture du canvas Three.js
            const imageDataUrl = this.threejsCanvas.toDataURL(mimeType, quality);

            // Debug: vérifier si l'image est toute noire
            this.debugCanvasContent(imageDataUrl);

            // ÉTAPE 4: Nettoyer le background temporaire
            if (temporaryBackgroundCreated) {
                this.removeTemporaryVideoBackground();
            }

            // ÉTAPE 5: Sauvegarder avec le bon nom de fichier
            this.savePictureWithExtension(imageDataUrl, extension);

        } catch (error) {
            // Nettoyer en cas d'erreur
            this.removeTemporaryVideoBackground();
        }
    }

    /**
     * Debug: Analyse le contenu du canvas
     */
    debugCanvasContent(dataUrl) {
        // Method kept for backwards compatibility but console outputs removed
    }

    /**
     * Sauvegarde l'image avec une extension spécifique
     */
    savePictureWithExtension(dataUrl, extension) {
        const link = document.createElement('a');
        link.style.display = 'none';
        
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        link.download = `threejs_screenshot_${timestamp}.${extension}`;
        link.href = dataUrl;
        
        document.body.appendChild(link);
        link.click();
        
        setTimeout(() => {
            document.body.removeChild(link);
        }, 100);
    }

    /**
     * Méthode alternative de capture utilisant le renderer Three.js directement
     * Utilisable avec clic droit sur le bouton photo
     */
    async takePictureAlternative(format = 'png', quality = 1.0) {
        try {
            if (!this.threeScene || !this.threeScene.renderer) {
                return;
            }

            this.animatePictureButton();

            // Créer le background temporaire si nécessaire
            const temporaryBackgroundCreated = await this.createTemporaryVideoBackground();
            
            if (temporaryBackgroundCreated) {
                await new Promise(resolve => setTimeout(resolve, 300));
            }

            // Forcer un rendu explicite
            this.threeScene.renderer.render(this.threeScene.scene, this.threeScene.camera);

            // Attendre que le rendu soit terminé
            await new Promise(resolve => requestAnimationFrame(resolve));

            // Obtenir le canvas du renderer
            const rendererCanvas = this.threeScene.renderer.domElement;
            
            // Format et capture
            let mimeType, extension;
            switch (format.toLowerCase()) {
                case 'jpeg':
                case 'jpg':
                    mimeType = 'image/jpeg';
                    extension = 'jpg';
                    break;
                case 'webp':
                    mimeType = 'image/webp';
                    extension = 'webp';
                    break;
                default:
                    mimeType = 'image/png';
                    extension = 'png';
                    quality = 1.0;
            }

            const imageDataUrl = rendererCanvas.toDataURL(mimeType, quality);
            
            // Nettoyer et sauvegarder
            if (temporaryBackgroundCreated) {
                this.removeTemporaryVideoBackground();
            }

            this.savePictureWithExtension(imageDataUrl, extension);

        } catch (error) {
            this.removeTemporaryVideoBackground();
        }
    }

    /**
     * Sauvegarde l'enregistrement
     */
    saveRecording() {
        if (this.recordedChunks.length === 0) {
            this.resetButton();
            return;
        }

        const blob = new Blob(this.recordedChunks, {
            type: this.mediaRecorder.mimeType || 'video/webm'
        });

        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const extension = this.getFileExtension(this.mediaRecorder.mimeType);
        a.download = `threejs_recording_${timestamp}.${extension}`;
        
        document.body.appendChild(a);
        a.click();
        
        setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }, 100);

        this.resetButton();
    }

    /**
     * Met à jour le bouton en mode "Stop"
     */
    updateButtonToStop() {
        this.recordButton.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style="vertical-align: middle; margin-right: 4px;"><rect x="4" y="4" width="16" height="16"/></svg>Stop';
        this.recordButton.style.backgroundColor = '#666666';
        this.recordButton.style.color = '#ffffff';
        this.recordButton.title = 'Stop recording';
    }

    /**
     * Remet le bouton à son état initial
     */
    resetButton() {
        this.recordButton.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style="vertical-align: middle; margin-right: 4px;"><circle cx="12" cy="12" r="8"/></svg>Record';
        this.recordButton.style.backgroundColor = '#000000';
        this.recordButton.style.color = '#ffffff';
        this.recordButton.title = 'Record';
        this.isRecording = false;
    }

    /**
     * Obtient l'extension de fichier basée sur le MIME type
     */
    getFileExtension(mimeType) {
        if (!mimeType) return 'webm';
        if (mimeType.includes('mp4')) return 'mp4';
        if (mimeType.includes('webm')) return 'webm';
        if (mimeType.includes('quicktime')) return 'mov';
        return 'webm'; // Fallback
    }

    /**
     * Nettoie et supprime les boutons
     */
    destroy() {
        if (this.isRecording) {
            this.stopRecording();
        }
        
        // S'assurer que le background temporaire est supprimé
        this.removeTemporaryVideoBackground();
        
        if (this.recordButton) {
            document.body.removeChild(this.recordButton);
            this.recordButton = null;
        }
        
        if (this.pictureButton) {
            document.body.removeChild(this.pictureButton);
            this.pictureButton = null;
        }
    }
}
